import { useState, useEffect, Fragment } from "react";
import moment from "moment";
import { getBuildMeta } from "./buildMeta.js";

const buildDateGreaterThan = (latestDate, currentDate) => {
  const format = "YYYY.MM.DD.HH.mm"; //the default format for the build date
  const formatRegex = /^\d{4}\.\d{2}\.\d{2}\.\d{2}\.\d{2}$/; //the test for the default format

  const latestDateFormat = formatRegex.test(latestDate) ? format : undefined; //if it doesn't match than it's a unix timestamp and doesn't need a format
  const currentDateFormat = formatRegex.test(currentDate) ? format : undefined; //if it doesn't match than it's a unix timestamp and doesn't need a format

  const momLatestDateTime = moment(latestDate, latestDateFormat); //format the str to a moment object
  const momCurrentDateTime = moment(currentDate, currentDateFormat); //format the str to a moment object

  if (momLatestDateTime.isAfter(momCurrentDateTime)) {
    //compare the two moment objects
    return true;
  } else {
    return false;
  }
};

function withClearCache(Component) {
  function ClearCacheComponent(props) {
    const [isLatestBuildDate, setIsLatestBuildDate] = useState(false);
    const [checkBuild, setCheckBuild] = useState(true);
    const [currentVersionDate, setCurrentVersionDate] = useState(() => {
      const { buildDate } = getBuildMeta();
      return buildDate || null;
    });

    useEffect(() => {
      const interval = setInterval(() => {
        setCheckBuild(true);
      }, 3600000);
      return () => {
        clearInterval(interval);
      };
    }, []);

    useEffect(() => {
      const isProd =
        import.meta.env.PROD ||
        import.meta.env.MODE === "production" ||
        process.env.NODE_ENV === "production";
      if (isProd && checkBuild && props.online) {
        try {
          const baseUrl = import.meta.env.BASE_URL || "/";
          fetch(`${baseUrl}meta.json`, {
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            cache: "no-cache",
          })
            .then(response => {
              return response.json();
            })
            .then(meta => {
              const latestVersionDate = meta?.buildDate;
              const { buildDate: buildDateFromEnv } = getBuildMeta();
              const resolvedCurrentVersion =
                buildDateFromEnv || latestVersionDate || null;
              setCurrentVersionDate(resolvedCurrentVersion);
              if (!latestVersionDate || !resolvedCurrentVersion) {
                setIsLatestBuildDate(true);
                setCheckBuild(false);
                return;
              }
              const shouldForceRefresh = buildDateGreaterThan(
                latestVersionDate,
                resolvedCurrentVersion
              );
              if (shouldForceRefresh) {
                console.log("Forcing refresh to fetch a new build");
                console.log("Your version: ", resolvedCurrentVersion);
                console.log("Latest version: ", latestVersionDate);
                setIsLatestBuildDate(false);
                refreshCacheAndReload();
              } else {
                console.log("Build is current");
                console.log("Your version: ", resolvedCurrentVersion);
                console.log("Latest version: ", latestVersionDate);
                setIsLatestBuildDate(true);
                setCheckBuild(false);
              }
            })
            .catch(error => {
              //force a refresh just in case
              setIsLatestBuildDate(false);
              refreshCacheAndReload();
              console.error(
                "There was a problem fetching the latest build information from the server:",
                error
              );
            });
        } catch (error) {
          console.error(
            "There was am issue fetching the latest build information from the server:",
            error
          );
        }
      } else {
        setIsLatestBuildDate(true);
        setCheckBuild(false);
      }
    }, [checkBuild, props.online]);

    const refreshCacheAndReload = () => {
      if (caches) {
        // Service worker cache should be cleared with caches.delete()
        caches.keys().then(names => {
          for (const name of names) {
            caches.delete(name);
          }
        });
      }
      // delete browser cache and hard reload
      window.location.reload(true);
    };

    return (
      <Fragment>
        {isLatestBuildDate ? (
          <Component currentVersionDate={currentVersionDate} {...props} />
        ) : null}
      </Fragment>
    );
  }

  return ClearCacheComponent;
}

export default withClearCache;
